// import dynamic from "next/dynamic";
// const { JSEncrypt } = dynamic(() => import("jsencrypt"), {
//   ssr: false,
// });
import { XtLocaleProps } from "@xt/xtbase";
import { XtLocale } from "@xt/xtbase";
import { FormInstance } from "antd/es/form/Form";
import { Router } from "next/router";
import sessionStorage from "utils/sessionStorage";
import type { FieldData } from "rc-field-form/lib/interface";
import { InternalNamePath, NamePath } from "antd/es/form/interface";
import { isEqual } from "lodash";
import moment from "moment";
import BigNumber from "bignumber.js";

/**
 * 根据当前顶级域名，获取二级域名链接
 * @param api，对应二级域名字符串，为空时返回顶级域名
 * @param noProtocol，获取链接默认是带有协议的，如果该字段存在，不包含协议
 *     如果 noProtocol === "ws" 会更加当前http协议添加 ws:// 或 wss://
 */
export function getHosts() {
  let host: string, protocol: string;
  if (typeof window === "undefined") {
    //server side
    host = process.env.NEXT_PUBLIC_host!;
    protocol = process.env.NEXT_PUBLIC_protocol!;
  } else {
    const isLocal = location.hostname === "localhost" || /^((25[0-5]|(2[0-4]|1\d|[1-9]|)\d)(\.(?!$)|$)){4}$/.test(location.hostname);
    host = isLocal ? process.env.NEXT_PUBLIC_host! : location.host;
    protocol = isLocal ? process.env.NEXT_PUBLIC_protocol! : location.protocol;
  }

  return { host, protocol };
}
export function getOrigin(api?: string, noProtocolOrWs?: string) {
  const { host, protocol } = getHosts();

  const topDomain = host.split(".").slice(-2).join(".");
  const domain = (api ? `${api}.` : "") + topDomain;
  if (!noProtocolOrWs) return `${protocol}//` + domain;
  if (noProtocolOrWs === "ws") return `${protocol.includes("https") ? "wss" : "ws"}://` + domain;
  return domain;
}

export function getServerSideDomain(arg: { inner: any; noLocalDomain: any; local: string }) {
  let domain = arg.inner;
  if (process.env.NEXT_PUBLIC_LOCAL) {
    if (arg.noLocalDomain) {
      domain = arg.local;
    } else {
      const { host, protocol } = getHosts();
      domain = protocol + "//" + host + arg.local;
    }
  }
  return domain;
}

/**
 * 正则表达式校验
 * @param type，验证类型，譬如：email 邮箱
 * @param content，需要验证的字符串
 * @param retRegExp，可选，如果存在，返回正则表达式
 * @returns，Boolean 是否匹配，或是是正则表达式
 */
export function regexp(type: string | number, content: string, retRegExp?: RegExp) {
  const verify = {
    email: /^([a-zA-Z0-9_\.-])+@([a-z0-9A-Z]+(-[a-z0-9A-Z]+)*\.)+[a-zA-Z]{2,}$/,
    phone: /^[0-9]{5,11}$/,
    loginPassword: /^(?!.*\s)(?!^[\u4e00-\u9fa5]+$).{8,1000}$/,
    securityPassword: /^(?![\d]+$)(?![a-zA-Z]+$)(?![!@#\$%\^&\*\(\)]+$)[\da-zA-Z!@#\$%\^&\*\(\)]{6,20}$/,
    securityPassword2: /^(?![\d]+$)(?![a-zA-Z]+$)(?![!@#\$%\^&\*\(\)]+$)[\da-zA-Z!@#\$%\^&\*\(\)]{2,20}$/,
  };

  if (!verify[type]) return;

  if (retRegExp) return verify[type];

  return verify[type].test(content);
}

/**
 * 加密字符串
 * @param key，getPublicKey获得的密钥
 * @param value，被加密字符串
 * @returns {string}，加密后的文本
 */
export function doRsaEncrypt(key: string, value: string): string {
  if (typeof window === "undefined") return "";
  if (!key || !value) return value;
  // console.info("doRsaEncrypt===============");
  const { JSEncrypt } = require("jsencrypt");
  // console.info("JSEncrypt =", JSEncrypt);
  const encrypt = new JSEncrypt();
  encrypt.setPublicKey(key);
  const encrypted = encrypt.encrypt(value);
  return encrypted;
}

/**
 * 将 text 文本复制到剪切板
 * @param text，需要复制的文本
 * 这个是同步函数，返回 true 说明复制成功，false 失败
 */
export function copy(text: string) {
  if (typeof window === "undefined") return;
  const ele = document.createElement("textarea");
  ele.readOnly = true;
  ele.style.position = "fixed";
  ele.style.opacity = "0";
  ele.style.width = "0";
  ele.style.height = "0";
  ele.value = text;
  document.body.appendChild(ele);
  ele.focus();
  //ele.select(); //not working in mobile
  ele.setSelectionRange(0, ele.value.length);
  const result = document.execCommand("copy");
  ele.parentNode!.removeChild(ele);
  return result;
}

/**
 * 获取带域名的绝对路径
 * @param {string} pathname, 路径地址，譬如 /login，
 * @param {string} locale, 语言路径，可选，譬如 zh-CN
 * @param {object} query, 链接参数，可选，譬如 {a: 1, b:2}
 * @param {string} basePath, 链接前缀，可选，默认 accounts
 */
interface ParamsAbsoluteUrl {
  pathname?: string;
  locale?: string;
  query?: ObjAny;
  basePath?: string;
}
export function getAbsoluteUrl({ pathname, locale, query, basePath = "accounts" }: ParamsAbsoluteUrl) {
  const { host, protocol } = getHosts();
  let domain = protocol + "//" + host;
  /*
  let domain =
    process.env.NEXT_PUBLIC_protocol + "//" + process.env.NEXT_PUBLIC_host;
   */
  if (
    process.env.DEBUG ||
    (typeof location !== "undefined" && (location.hostname === "localhost" || /^((25[0-5]|(2[0-4]|1\d|[1-9]|)\d)(\.(?!$)|$)){4}$/.test(location.hostname)))
  ) {
    domain = "http://localhost:8081";
  }

  let url = domain + (locale ? `/${locale}` : "") + (basePath ? `/${basePath}` : "") + pathname;

  if (query) {
    let search = "";
    for (const va in query) {
      search += (search ? "&" : "") + va + "=" + encodeURIComponent(query[va]);
    }
    if (search) {
      if (url.includes("?")) {
        if (url[url.length - 1] === "?") {
          url += search;
        } else {
          url += "&" + search;
        }
      } else {
        url += "?" + search;
      }
    }
  }

  return url;
}

/**
 * 重定向，对满足条件的链接进行重定向
 * @router
 */
export function redirectBackUrl(
  router: Router,
  {
    noElse = false, //如果 backurl 不符合条件，就没有下文了，否则重定向到默认地址
    method = "replace", //重定向方法
    defaultArgs = undefined, //默认重定向内容
  } = {}
) {
  if (sessionStorage.get("oauth2")) {
    return router.replace({
      pathname: "/accounts/oauth2",
      query: router.query,
    });
  }

  const backurl = router.query.backurl || router.query.backUrl || getUrlSearchAttr("backurl");

  if (backurl) {
    const topDomain = getOrigin("", "none");
    const regExp = new RegExp("^(http://|https://)(\\w+\\.)?" + topDomain + "($|/.*|\\?.*)", "i");
    const isLegal = regExp.test(backurl as string); //是否为合法回调地址
    if (isLegal) {
      if (typeof location !== "undefined") {
        if (method === "replace") {
          location.replace(backurl as string);
        } else {
          location.href = backurl as string;
        }
      } else {
        router[method](backurl);
      }

      return;
    }
  }

  if (noElse) return;

  let url = defaultArgs || getOrigin("www") + "/" + (router.locale || "");
  if (typeof location !== "undefined") {
    if (typeof url === "object") url = getAbsoluteUrl({ locale: router.locale, ...(url as any) });
    if (method === "replace") {
      location.replace(url);
    } else {
      location.href = url;
    }
  } else {
    router[method](url);
  }
}

/**
 * 登录成功后统一跳转首页（replace 避免历史栈中保留登录页，用户点返回不会回到登录页）
 * 若需恢复「按 backurl 或默认地址跳转」，可改用 redirectBackUrl(router)
 */
export function redirectToHome(router: Router) {
  const url = getOrigin("www") + "/" + (router.locale || "");
  if (typeof location !== "undefined") {
    location.replace(url);
  } else {
    router.replace(url);
  }
}

export function getExplore() {
  const Sys: ObjAny = {};
  const ua = navigator.userAgent.toLowerCase();
  let s: any[] | null;
  (s = ua.match(/rv:([\d.]+)\) like gecko/))
    ? (Sys.ie = s[1])
    : (s = ua.match(/msie ([\d\.]+)/))
    ? (Sys.ie = s[1])
    : (s = ua.match(/edg\/([\d\.]+)/))
    ? (Sys.edge = s[1])
    : (s = ua.match(/firefox\/([\d\.]+)/))
    ? (Sys.firefox = s[1])
    : (s = ua.match(/(?:opera|opr).([\d\.]+)/))
    ? (Sys.opera = s[1])
    : (s = ua.match(/chrome\/([\d\.]+)/))
    ? (Sys.chrome = s[1])
    : (s = ua.match(/version\/([\d\.]+).*safari/))
    ? (Sys.safari = s[1])
    : 0;
  // 根据关系进行判断
  if (Sys.ie) return "IE V" + Sys.ie;
  if (Sys.edge) return "EDGE V" + Sys.edge;
  if (Sys.firefox) return "Firefox V" + Sys.firefox;
  if (Sys.chrome) return "Chrome V" + Sys.chrome;
  if (Sys.opera) return "Opera V" + Sys.opera;
  if (Sys.safari) return "Safari V" + Sys.safari;
  return "Unknown";
}

export function browserRedirect() {
  const sUserAgent = navigator.userAgent;
  const isWin = navigator.platform == "Win32" || navigator.platform == "Windows";
  const isMac = navigator.platform == "Mac68K" || navigator.platform == "MacPPC" || navigator.platform == "Macintosh" || navigator.platform == "MacIntel";
  if (isMac) return "Mac OS";
  const isUnix = navigator.platform == "X11" && !isWin && !isMac;
  if (isUnix) return "Unix";
  const isLinux = String(navigator.platform).indexOf("Linux") > -1;
  if (isLinux) return "Linux";
  if (isWin) {
    const isWin2K = sUserAgent.indexOf("Windows NT 5.0") > -1 || sUserAgent.indexOf("Windows 2000") > -1;
    if (isWin2K) return "Win2000";
    const isWinXP = sUserAgent.indexOf("Windows NT 5.1") > -1 || sUserAgent.indexOf("Windows XP") > -1;
    if (isWinXP) return "WinXP";
    const isWin2003 = sUserAgent.indexOf("Windows NT 5.2") > -1 || sUserAgent.indexOf("Windows 2003") > -1;
    if (isWin2003) return "Win2003";
    const isWinVista = sUserAgent.indexOf("Windows NT 6.0") > -1 || sUserAgent.indexOf("Windows Vista") > -1;
    if (isWinVista) return "WinVista";
    const isWin7 = sUserAgent.indexOf("Windows NT 6.1") > -1 || sUserAgent.indexOf("Windows 7") > -1;
    if (isWin7) return "Win7";
    const isWin10 = sUserAgent.indexOf("Windows NT 10") > -1 || sUserAgent.indexOf("Windows 10") > -1;
    if (isWin10) return "Win10";
  }
  return "Unknown";
}

export function getUUID() {
  let d = new Date().getTime();
  const uuid = "xxxxxxxx-xxxx-xxxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
    const r = (d + Math.random() * 16) % 16 | 0;
    d = Math.floor(d / 16);
    return (c == "x" ? r : (r & 0x3) | 0x8).toString(16);
  });
  return uuid;
}

/**
 * 获取url中search字段，譬如 getUrlSearchAttr('token')
 * @param regStr，属性字段
 * @param exact，是否精确匹配大小写
 * @param urlStr，如果存在，使用该字符串作为查找对象
 * @returns {string}，返回对应属性的值
 */
export function getUrlSearchAttr(regStr: string, urlStr?: string, exact?: any): string {
  if (!urlStr) {
    if (typeof window === "undefined") return "";
    urlStr = window.location.href;
  }
  urlStr.indexOf("?") < 0 && (urlStr = "?" + urlStr);
  const todoStr = urlStr;

  const regExp = new RegExp("[?&]" + regStr + "=[^&]+", exact ? "g" : "gi");
  let value = "",
    temp: string;
  todoStr.replace(regExp, (str): any => {
    temp = str.split("=")[1];
    temp && (value = decodeURIComponent(temp));
  });

  return value;
}

/**
 * 对象深拷贝
 * @param obj 需要拷贝的源对象
 */
export function deepClone<T>(obj: T): T {
  if (!obj || typeof obj !== "object") return obj;
  const result = Array.isArray(obj) ? [] : {};
  for (const key in obj) {
    // eslint-disable-next-line no-prototype-builtins
    if ((obj as ObjAny).hasOwnProperty(key)) {
      if (typeof obj[key] === "object" && obj[key] !== null) {
        (result as ObjAny)[key] = deepClone(obj[key]); //递归复制
      } else {
        (result as ObjAny)[key] = obj[key];
      }
    }
  }
  return result as T;
}

/**
 * 外部使用翻译函数XtLocale必须init
 * @param {string} key
 * @param {object} params
 * @returns {string}
 */
export function t(key: string, params?: any): string {
  if (!XtLocale.intl) return "";
  if (!key) return "";
  const { formatMessage } = XtLocale.intl;
  let message = formatMessage({ id: key }, params, { ignoreTag: true });
  if (message.startsWith("$t(")) {
    //react-i18next:$t(anotherkey)
    const key1 = message.substring(3, message.length - 1);
    message = formatMessage({ id: key1 }, params, { ignoreTag: true });
  }
  return message;
}

/**
 * axios请求节流函数
 * 使用时fn第一个参数必须是解锁函数
 * @param {(unlock:()=>any,...args)=>any} fn 主逻辑函数
 * @param {number} delay 自动解锁时间
 * @returns {function(...args):void} 返回新的函数
 */
export function axiosThrottle(fn: (unlock: () => any, ...args: any) => any, delay = 500): (...rest: any[]) => void {
  const task = { value: false };
  let unLockTimer: Timeout = null;
  const unlock = () => {
    if (task.value) {
      task.value = false;
    }
  };
  return function (this: any, ...args) {
    if (task.value) return;
    task.value = true;
    fn.apply(this, [unlock, ...args]);
    // 一定时间之后自动解锁
    if (unLockTimer) clearTimeout(unLockTimer);
    unLockTimer = setTimeout(() => {
      unlock();
    }, delay);
  };
}

export function sensorsTrack(eventName: string, config = {}) {
  try {
    sensors.track(eventName, config);
  } catch (e) {
    console.log("sensors error", e);
  }
}

export function getUrlParamByName(url: string, name: string): string {
  const reg = new RegExp("(^|\\?|&)" + name + "=([^&]*)(&|$)", "i");
  const r = url ? url.match(reg) : window.location.href.match(reg);
  if (r != null) {
    return decodeURIComponent(r[2]);
  }
  return "";
}

export function getUrlParamByNames(url: string, nameList: string[]) {
  const searchObj = nameList.reduce((p, c) => {
    p[c] = getUrlParamByName(url, c);
    return p;
  }, {});
  return searchObj;
}

/**
 *
 * @param {string} url
 * @returns {object} eg:{a:1,b:2}
 */
export function getSearch(url?: string): ObjAny {
  let newUrl = "";
  if (!url && typeof window != "undefined") {
    newUrl = window.location.href;
  }
  const params = {};
  const arr = newUrl.split("?");
  if (arr.length <= 1) return params;
  arr[1].split("&").forEach((item) => {
    params[item.split("=")[0]] = isNaN(Number(item.split("=")[1])) ? item.split("=")[1] : +item.split("=")[1];
  });
  return params;
}

/**
 * [过滤对象]
 * @param  obj [过滤前数据]
 * @param  cond [过滤条件，要求为数组或者callback根据返回值是否为true来过滤类似于数组filter]
 */
export function pickObj<T extends ObjAny>(obj: T, cond: (keyof T)[] | ((value: T[keyof T], k: keyof T) => boolean)): T {
  const isRightArg2 = Array.isArray(cond) || typeof cond === "function";
  if (typeof obj !== "object" || !obj || !isRightArg2) {
    throw new Error("参数格式不正确");
  }
  const result = {} as T;
  if (Array.isArray(cond)) {
    Object.keys(obj)
      .filter((key) => cond.includes(key))
      .forEach((key) => {
        result[key as keyof T] = obj[key];
      });
  } else {
    for (const k in obj) {
      const res = cond(obj[k], k);
      if (res) result[k] = obj[k];
    }
  }

  return deepClone(result);
}

/**
 * [过滤对象]
 * @param  obj [过滤前数据]
 * @param  cond [过滤条件，要求为数组或者callback根据返回值是否为true来过滤类似于数组filter] 在名单中或者返回值为true就排除掉
 */
export function omitObj<T extends ObjAny>(obj: T, cond: (keyof T)[] | ((value: T[keyof T], k: keyof T) => boolean)): T {
  const isRightArg2 = Array.isArray(cond) || typeof cond === "function";
  if (typeof obj !== "object" || !obj || !isRightArg2) {
    throw new Error("参数格式不正确");
  }
  const result = {} as T;
  if (Array.isArray(cond)) {
    Object.keys(obj)
      .filter((key) => !cond.includes(key))
      .forEach((key) => {
        result[key as keyof T] = obj[key];
      });
  } else {
    for (const k in obj) {
      const res = cond(obj[k], k);
      if (!res) result[k] = obj[k];
    }
  }

  return deepClone(result);
}

/**
 * 针对复杂表单数据，例如嵌套结构 数组结构  对其进行更改change之后的value,用于手动双向数据绑定
 * @param formData 表单数据
 * @param changedFields onFieldsChange中的第一个参数发生改变的表单域
 * @returns 更改后的表单数据
 */
let cacheFields = [] as { value: any; name: NamePath }[];
export function setFormValue<T extends ObjAny>(formData: T, changedFields: FieldData[]): T | null {
  const compare = (name: NamePath, value: any, i: number) => {
    const nameStr = Array.isArray(name) ? name.join("") : name + "";
    const cacheNameStr = Array.isArray(cacheFields[i].name) ? (cacheFields[i].name as InternalNamePath).join("") : cacheFields[i].name + "";
    if (nameStr === cacheNameStr && isEqual(value, cacheFields[i].value)) return true;
    return false;
  };
  if (cacheFields.length === changedFields.length) {
    const bool = changedFields.every((item, i) => compare(item.name, item.value, i));
    if (bool) return null;
  }
  cacheFields = changedFields.map((item) => ({ name: item.name, value: item.value }));
  const data = deepClone(formData) as ObjAny;
  changedFields.forEach((field) => {
    if (typeof field.name === "string" || typeof field.name === "number") {
      data[field.name] = field.value;
    } else {
      // 把namePath数组形式转为字符串path
      const path = field.name.map((v, i) => (typeof v === "number" ? `[${v}]` : i > 0 ? `.${v}` : v)).join("");
      new Function("data", "value", `return data.${path} = value`)(data, field.value);
    }
  });
  return data as T;
}

// 加密邮箱
export function encryptionEmail(email: string) {
  return email.replace(/(\w{2}).*(\w{1})@(.*)/, "$1***@$3");
}
// 加密手机
export function encryptionMobile(mobile: string) {
  return mobile.length > 7 ? mobile.replace(/(\d{3})\d*(\d{4})/, "$1***$2") : mobile.replace(/(\d{2})\d*(\d{2})/, "$1***$2");
}

// 日期格式处理
export function formatDate(date, format = "YYYY-MM-DD HH:mm:ss") {
  return date ? moment(date).format(format) : "--";
}

export function encryptionStr(str, num = 10) {
  if (!str || str.length <= 2) return str;
  const cutLength = str.length / 2;
  const floorNum = Math.floor(cutLength);
  const subStr1 = cutLength > num ? str.substr(0, num) : str.substr(0, floorNum);
  const subStr2 = cutLength > num ? str.substr(str.length - num, num) : str.substr(str.length - floorNum, floorNum);
  return `${subStr1}...${subStr2}`;
}

/**
 * 创建ele元素
 * @parEle 父节点
 * @tag Sting，标签字符串
 * @argObj Object，参数
 */
export function $cre(parEle, tag, argObj) {
  !tag && (tag = "div");
  if (typeof tag !== "string") return;

  const ele = document.createElement(tag);

  if (argObj && typeof argObj === "object") {
    for (const va in argObj) {
      if (va === "style" && typeof argObj[va] === "object") {
        for (const vaSub in argObj[va]) ele[va][vaSub] = argObj[va][vaSub];
      } else {
        ele[va] = argObj[va];
      }
    }
  }

  if (parEle) {
    if (typeof parEle === "string") parEle = document.getElementById(parEle);
    parEle.appendChild && parEle.appendChild(ele);
  }

  return ele;
}

/**
 * 监听ele元素的尺寸变化
 * @ele DOM Element，不存在默认 documentElement
 * @callback 回调函数，参数 target Element
 */
export function eleResizeObserver(ele, callback) {
  try {
    const resizeObserver = new ResizeObserver((entries) => {
      // console.log("%c【method.js】resizeObserver %o", "color:#00aecc", entries[0].target);
      callback(entries[0].target);
    });
    resizeObserver.observe(ele || document.documentElement);
  } catch (e) {
    const iframe = $cre(ele || document.body, "iframe", {
      style: {
        position: "absolute",
        zIndex: "-1",
        top: "0",
        bottom: "0",
        left: "0",
        height: "100%",
        width: "100%",
        border: "none",
        opacity: "0",
        background: "transparent",
      },
    }) as any;
    iframe.contentWindow.onresize = function (e) {
      console.log("%c【method.js】iframe.onresize %o", "color:#00aecc", e.target.document.documentElement);
      callback(e.target.document.documentElement);
    };
  }
}

/**
 * 千分位分隔符函数，用于对数字字符串进行千分位分隔，且不改变小数位
 * strOrNum 数字或者字符串
 */
export function thousands(strOrNum: string | number): string {
  const ary = ((strOrNum || 0) + "").split(".");
  const retAry = [ary[0].replace(/\d(?=(\d{3})+$)/g, "$&,")];
  ary[1] && retAry.push(ary[1]);
  return retAry.join(".");
}

export const handleBigNumber = (value) => {

  const v = new BigNumber(value);
  if (v.eq(0)) return 0;
  if (v.gte(1000000000) || v.lte(-1000000000)) {
    return v.div(1000000000) + "B";
  }
  if (v.gte(1000000) || v.lte(-1000000)) {
    return v.div(1000000) + "M";
  }
  if (v.gte(1000) || v.lte(-1000)) {
    return v.div(1000) + "K";
  }
  return v;
};

/**
 * Zendesk Help Center URL 路径段（如 en-us、pt-br），与 ssr-web-index / web-index getZenLan 对齐。
 * Next router.locale / Register langCode：en | pt | ar | zh-CN …
 */
export function getZendeskHcLocale(routerLocale: string | undefined): string {
  if (!routerLocale) return "en-us";
  const zenLan: Record<string, string> = {
    en: "en-us",
    pt: "pt-br",
    ar: "ar",
    "zh-CN": "zh-cn",
    "zh-HK": "zh-tw",
    cn: "zh-cn",
    hk: "zh-tw",
    ja: "ja",
    ko: "ko-kr",
    ru: "ru",
    vi: "vi-vn",
    th: "th",
  };
  return zenLan[routerLocale] || "en-us";
}

/** Zendesk widget 语言：默认英文，禁止中文，与 ssr-web-trade/ssr-web-index 一致 */
export function getZendeskLocale(zenLan: string | undefined): string {
  if (!zenLan || typeof zenLan !== "string") return "en";
  const locale = zenLan.toLowerCase().replace(/_/g, "-");
  if (locale.startsWith("zh")) return "en";
  return locale;
}
